import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Cake,
  Package,
  Truck,
  LogOut,
  MessageCircle,
  Briefcase,
  Wallet,
  Images,
  LayoutDashboard,
} from "lucide-react";
import { toast } from "sonner";
import { requestManageSession } from "@/lib/manageSession";
import { MANAGER_TOKEN_KEY } from "@/lib/authSession";
import { canAccessDesignGallery, canAccessManage } from "@/lib/tsIdentity";
import { applyStaffPortalDocument, enterStaffPortal } from "@/lib/staffPortal";
import TsSessionGate from "@/components/mobile/TsSessionGate";
import TsOnboarding from "@/components/mobile/TsOnboarding";
import { TS_DEPARTMENT_LABELS } from "@/components/mobile/TsDepartmentPicker";
import {
  clearTsSession,
  completeTsOnboarding,
  filterPortionsByDepartments,
  loadTsDepartments,
  loadTsPortionUrls,
  tsAppPortionPaths,
  tsVisiblePortions,
} from "@/lib/tsIdentity";
import {
  isTeamHubHost,
  staffBrandKicker,
  staffBrandTitle,
  teamHomePath,
  TEAM_APP_PATHS,
} from "@/lib/teamHub";
import { OPS_BASENAME } from "@/lib/opsBase";

const PINK = "#EE2677";

const PORTION_ICONS = {
  kitchen: Cake,
  driver: Truck,
  inventory: Package,
  chat: MessageCircle,
  salary: Wallet,
  we: Briefcase,
};

function TsHomeBody({ identity: initialIdentity }) {
  const navigate = useNavigate();
  const [identity, setIdentity] = useState(initialIdentity);
  const [err, setErr] = useState("");
  const [showReviewSetup, setShowReviewSetup] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [manageBusy, setManageBusy] = useState(false);

  useEffect(() => {
    const home = teamHomePath();
    enterStaffPortal("ts", isTeamHubHost() ? home : "/ts");
    applyStaffPortalDocument("ts");
  }, []);

  useEffect(() => {
    setIdentity(initialIdentity);
  }, [initialIdentity]);

  const handleLogout = () => {
    clearTsSession();
    window.location.href = isTeamHubHost() ? teamHomePath() : "/ts";
  };

  const openPortion = (path) => {
    navigate(path);
  };

  /**
   * Trades this TS session for a short manager token, so an admin never types
   * the shared password. The server re-resolves the role instead of trusting
   * the token claim, so someone demoted since login is refused here even while
   * holding a token that still says admin.
   *
   * Ops is at /ops on this origin now, so the token is simply stored. The
   * HttpOnly parent-domain cookie this used to need existed only because Team
   * and Manage were separate origins and localStorage is per-origin; it went
   * away with the origin split.
   *
   * A full load rather than navigate(): /ops mounts the router under a
   * different basename, and that only takes effect on a fresh mount.
   */
  const openOps = async () => {
    setManageBusy(true);
    setErr("");
    try {
      const token = await requestManageSession();
      localStorage.setItem(MANAGER_TOKEN_KEY, token);
      window.location.href = OPS_BASENAME;
    } catch (ex) {
      setErr(ex.message || "Could not open Ops");
      toast.error(ex.message || "Could not open Ops");
      setManageBusy(false);
    }
  };

  const handleReviewSave = ({ name, departments }) => {
    if (!identity) return;
    setReviewBusy(true);
    setErr("");
    try {
      const depts =
        identity.is_ts_admin || (identity.available_departments || []).length <= 1
          ? identity.available_departments || []
          : departments;
      completeTsOnboarding(identity, { name, departments: depts });
      setIdentity({ ...identity, name: (name || identity.name || "").trim() });
      setShowReviewSetup(false);
    } catch (ex) {
      setErr(ex.message || "Could not save setup");
    } finally {
      setReviewBusy(false);
    }
  };

  if (showReviewSetup && identity) {
    return (
      <TsOnboarding
        mode="review"
        identity={identity}
        initialDepartments={loadTsDepartments()}
        onComplete={handleReviewSave}
        onCancel={() => setShowReviewSetup(false)}
        busy={reviewBusy}
      />
    );
  }

  const allPortions = tsVisiblePortions(identity, loadTsPortionUrls());
  const portions = filterPortionsByDepartments(allPortions, loadTsDepartments(), identity);
  // A staff-role person still sees Salary, so an empty grid means their role
  // grants nothing at all — say so rather than showing a blank page.
  const hasNoTiles =
    !portions.length && !canAccessManage(identity) && !canAccessDesignGallery(identity) && !identity?.can_chat;
  const displayName = (identity?.name || "").trim();
  const selectedDepts = loadTsDepartments();
  const deptSummary =
    identity?.is_ts_admin || !selectedDepts.length
      ? null
      : selectedDepts.map((k) => TS_DEPARTMENT_LABELS[k] || k).join(" · ");

  return (
    <div className="ts-home">
      <style>{`
        .ts-home {
          min-height: 100dvh; background: #F5F2EA; color: #2D2D2D;
          font-family: system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
          padding: 16px 14px 32px; max-width: 480px; margin: 0 auto;
        }
        .ts-home * { box-sizing: border-box; }
        .ts-home-header { text-align: center; margin: 20px 0 28px; }
        .ts-home-kicker {
          font-size: 11px; font-weight: 800; letter-spacing: 0.12em;
          text-transform: uppercase; color: rgba(45,45,45,0.45); margin: 0 0 4px;
        }
        .ts-home-title { font-size: 32px; font-weight: 900; color: ${PINK}; margin: 0; }
        .ts-home-greet {
          margin: 8px 0 0; font-size: 15px; font-weight: 700; color: rgba(45,45,45,0.65);
        }
        .ts-home-depts {
          margin: 6px 0 0; font-size: 12px; font-weight: 700; color: rgba(45,45,45,0.5);
        }
        .ts-home-setup-links {
          margin-top: 6px; display: flex; flex-direction: column; align-items: center; gap: 2px;
        }
        .ts-home-setup-link {
          border: none; background: transparent; color: ${PINK};
          font-size: 13px; font-weight: 800; cursor: pointer; padding: 4px 0;
        }
        .ts-home-grid { display: grid; gap: 12px; }
        .ts-home-tile {
          display: flex; align-items: center; gap: 14px;
          background: #fff; border: none; border-radius: 18px; padding: 18px 16px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.05); cursor: pointer; text-align: left; width: 100%;
        }
        .ts-home-tile-icon {
          width: 48px; height: 48px; border-radius: 14px; background: #FFF0F6;
          display: flex; align-items: center; justify-content: center; color: ${PINK}; flex-shrink: 0;
        }
        .ts-home-tile-label { font-size: 18px; font-weight: 800; color: #2D2D2D; }
        .ts-home-tile-sub { font-size: 13px; font-weight: 600; color: rgba(45,45,45,0.5); margin-top: 2px; }
        .ts-home-logout {
          margin-top: 24px; width: 100%; border: none; background: transparent;
          color: rgba(45,45,45,0.5); font-size: 14px; font-weight: 700; cursor: pointer;
          display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px;
        }
        .ts-home-err {
          background: #FEE2E2; color: #B91C1C; border-radius: 12px; padding: 12px;
          font-size: 14px; font-weight: 600; margin-bottom: 12px;
        }
        .ts-home-empty {
          background: #fff; border-radius: 14px; padding: 18px 16px; margin: 0 0 12px;
          font-size: 14px; font-weight: 600; color: rgba(45,45,45,0.6); text-align: center;
        }
        .ts-home-tile:disabled { opacity: 0.6; cursor: default; }
        .ts-home-tile-primary {
          border: 2px solid ${PINK}; box-shadow: 0 4px 14px rgba(238,38,119,0.14);
        }
        .ts-home-tile-primary .ts-home-tile-icon { background: ${PINK}; color: #fff; }
      `}</style>

      <header className="ts-home-header">
        <p className="ts-home-kicker">{staffBrandKicker()}</p>
        <h1 className="ts-home-title">{staffBrandTitle()}</h1>
        {displayName ? <p className="ts-home-greet">Hi, {displayName}</p> : null}
        {identity?.employee_id ? (
          <p className="ts-home-depts" style={{ marginTop: 8 }}>
            Employee ID: {identity.employee_id}
          </p>
        ) : null}
        {deptSummary || !identity?.is_ts_admin ? (
          <div className="ts-home-setup-links">
            {deptSummary ? <p className="ts-home-depts">{deptSummary}</p> : null}
            <button type="button" className="ts-home-setup-link" onClick={() => setShowReviewSetup(true)}>
              Review setup
            </button>
          </div>
        ) : null}
      </header>

      {err ? <p className="ts-home-err">{err}</p> : null}

      {hasNoTiles ? (
        <p className="ts-home-empty" data-testid="ts-home-empty">
          No apps are assigned to you yet. Ask a manager to set your access role.
        </p>
      ) : null}

      <div className="ts-home-grid">
        {/* Manage sits first: it used to be the parent of everything, so for an
            admin it is the most likely destination on this screen. */}
        {canAccessManage(identity) ? (
          <button
            type="button"
            className="ts-home-tile ts-home-tile-primary"
            disabled={manageBusy}
            onClick={openOps}
            data-testid="ts-home-tile-manage"
          >
            <span className="ts-home-tile-icon">
              <LayoutDashboard size={24} aria-hidden />
            </span>
            <span>
              <div className="ts-home-tile-label">Ops</div>
              <div className="ts-home-tile-sub">
                {manageBusy ? "Opening…" : "Office console: orders, customers, finance"}
              </div>
            </span>
          </button>
        ) : null}
        {portions.map(({ key, label, path }) => {
          const Icon = PORTION_ICONS[key] || Package;
          const sub =
            key === "kitchen"
              ? "Production: baker, froster, decorator"
              : key === "driver"
                ? "Delivery board & handoff"
                : key === "chat"
                  ? "WhatsApp chat report handoff"
                  : key === "we"
                    ? "Team tasks, time & work ethic"
                    : "Stock bins & inventory";
          return (
            <button key={key} type="button" className="ts-home-tile" onClick={() => openPortion(path)}>
              <span className="ts-home-tile-icon">
                <Icon size={24} aria-hidden />
              </span>
              <span>
                <div className="ts-home-tile-label">{label}</div>
                <div className="ts-home-tile-sub">{sub}</div>
              </span>
            </button>
          );
        })}
        {identity?.can_chat ? (
          <button
            type="button"
            className="ts-home-tile"
            onClick={() => navigate(isTeamHubHost() ? TEAM_APP_PATHS.tc : "/ts/app/tc")}
          >
            <span className="ts-home-tile-icon">
              <MessageCircle size={24} aria-hidden />
            </span>
            <span>
              <div className="ts-home-tile-label">Chats</div>
              <div className="ts-home-tile-sub">WhatsApp inbox</div>
            </span>
          </button>
        ) : null}
        {canAccessDesignGallery(identity) ? (
          <button
            type="button"
            className="ts-home-tile"
            onClick={() => navigate(tsAppPortionPaths().gallery || "/ts/app/gallery")}
          >
            <span className="ts-home-tile-icon">
              <Images size={24} aria-hidden />
            </span>
            <span>
              <div className="ts-home-tile-label">Designs</div>
              <div className="ts-home-tile-sub">Cake design gallery</div>
            </span>
          </button>
        ) : null}
      </div>

      <button type="button" className="ts-home-logout" onClick={handleLogout}>
        <LogOut size={16} aria-hidden />
        {isTeamHubHost() ? "Sign out" : "Sign out of TS"}
      </button>
    </div>
  );
}

export default function TsHome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleReady = () => {
    if (!isTeamHubHost()) return;
    const raw = (searchParams.get("next") || "").trim();
    if (!raw.startsWith("/") || raw.startsWith("//") || raw.includes("://") || raw === "/") return;
    navigate(raw, { replace: true });
  };

  return (
    <TsSessionGate onReady={handleReady}>
      {({ identity }) => (identity ? <TsHomeBody identity={identity} /> : null)}
    </TsSessionGate>
  );
}
